import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';

import { Dynamic, type DynamicAction } from '@shared/registry/dynamic';

import { type SnapshotCardConfig } from './snapshot-card.types';

@Component({
  selector: 'app-snapshot-card',
  templateUrl: './snapshot-card.html',
  imports: [Dynamic],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnapshotCard {
  readonly config = input.required<SnapshotCardConfig>();

  readonly action = output<DynamicAction>();

  protected readonly hasBorder = computed(() => !!this.config().border);
  protected readonly canDismiss = computed(() => !!this.config().canDismiss);
  protected readonly snapshots = computed(() => this.config().snapshots);
  protected readonly subtext = computed(() => this.config().subtext);

  protected readonly chipConfig = computed(() => this.config().chip);

  protected readonly dismissButtonConfig = computed(() => ({
    icon: 'x',
    size: 'sm' as const,
  }));

  protected readonly subtextConfig = computed(() => ({
    text: this.subtext() ?? '',
    size: 'sm' as const,
    fontWeight: 'bold' as const,
    color: 'var(--snapshotcard-color-subtext)',
  }));

  protected readonly snapshotConfigs = computed(
    () =>
      this.snapshots()?.map((snapshot) => ({
        icon: snapshot.icon,
        labelConfig: {
          text: snapshot.label,
          size: 'sm' as const,
          fontWeight: 'bold' as const,
          color: 'var(--snapshotcard-color-subtext)',
        },
        valueConfig: {
          text: snapshot.value,
          size: 'xl' as const,
          fontWeight: 'semibold' as const,
          color: 'var(--snapshotcard-color-content)',
        },
      })) ?? [],
  );

  protected readonly lastSnapshotConfig = computed(() => {
    const configs = this.snapshotConfigs();
    return configs.length % 2 !== 0 ? configs[configs.length - 1] : null;
  });

  protected onDismiss(): void {
    this.action.emit(this.config().dismissAction ?? { id: 'dismiss' });
  }

  protected onButtonClick(event: DynamicAction): void {
    this.action.emit(event);
  }
}
